import { g as generateSearchUrlBase } from './generate-search-url-base.js';
import { g as getRoomType } from './get-room-type.js';
import './index2.js';

/**
 * **LetsRevenue** (another version of iHotelier/TravelClick by Amadeus)
 *
 * @exports bs_368
 * @category engines
 */
function factory({ window, document, thn }) {
    const engine = {};
    /**
       * @memberOf module:bs_368
       * @this thn.widgets.price
       * @see {@link Price#observe_dom}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceObserveDom = function priceObserveDom(ctx) {
        const usersShouldUseDataLayerObserver = [1044040].includes(thn.data.accountUserId);
        const dataLayer = window.dataLayer;
        const container = document.body;
        // dataLayer observer
        if (dataLayer && usersShouldUseDataLayerObserver) {
            const refreshPrice = ({ message }) => {
                if (message.ihResultsDisplayed) {
                    ctx.widget.update();
                }
            };
            thn.bus.on('dataLayer:push', refreshPrice, true);
            thn.pageUnload(() => {
                thn.bus.detach('dataLayer:push', refreshPrice);
            });
            // Dom observer
        }
        else if (container) {
            const priceRefresh = thn.debounce(() => {
                const loading = document.querySelector('.ibe-cn-site-loader');
                const roomsData = document.querySelector('amadeus-hos-res-wc-shop-rooms');
                if (!loading && roomsData) {
                    ctx.widget.refresh();
                }
            }, 600);
            thn.observeDom(container, priceRefresh, {
                childList: true,
                subTree: true,
                attributes: true,
                characterData: true,
            });
            thn.pageUnload(() => {
                priceRefresh.cancel();
                thn.stopObserveDom(container, priceRefresh);
            });
        }
    };
    /**
     * @memberOf module:bs_368
     * @see {@link generate_search_url}
     *
     * @param {object} ctx - The context object.
     */
    engine.generateSearchUrl = function generateSearchUrl(ctx) {
        // example1: https://www.paradoxhotels.com/vancouver/book/accommodations?_ga=2.112693379.663279728.1700140940-1166534872.1700140939&adults=1&datein=11/16/2023&dateout=11/17/2023&domain=www.paradoxhotels.com&languageid=1&rooms=1
        // example 2: https://shadehotel.ihotelier.com/redondo-beach/book/accommodations?thn=true&discount=THNEXCL&adults=2&children=0&rooms=1&DateIn=06%2F19%2F2024&DateOut=06%2F20%2F2024&languageid=en&currency=USD
        const url = new URL(ctx.baseUrl);
        const validUrl = url.pathname.match(/book|reservar/);
        if (!validUrl) {
            return { newUrl: '', newData: {} };
        }
        return generateSearchUrlBase(ctx);
    };
    /**
      *
      * @memberOf module:bs_368
      *
      */
    const roomDetailsParseData = (roomsEl = '') => {
        const roomsArray = roomsEl && [...roomsEl.querySelectorAll('.room-list-wrapper, .room-grid-card-wrapper')];
        return !thn.empty(roomsArray) && roomsArray.map((room) => {
            const roomName = room.querySelector('.room-name')?.getAttribute('aria-label');
            const roomType = getRoomType(roomName);
            const rateList = room.querySelector('amadeus-hos-res-wc-shop-rooms-rate-list')?.shadowRoot;
            const roomPrices = rateList && [...rateList.querySelectorAll('.rate-col')].map((rate) => {
                const rateId = rate.getAttribute('id');
                const rateName = rate.querySelector('.rate-plan-name amadeus-hos-res-ct-title-4')?.getAttribute('aria-label');
                const pricesContainer = rate.querySelector('.rate-section amadeus-hos-res-wc-shop-rooms-rate-display')?.shadowRoot;
                const ratePrice = pricesContainer && thn.parseNumber([...pricesContainer.querySelectorAll('.room-rates span')].pop()?.textContent);
                const hasBreakfast = rateName && (rateName.includes('Breakfast Included') || rateName.includes('Desayuno Incluido'));
                const isRefundable = rateName && rateName.includes('Flexible');
                return {
                    id: rateId,
                    name: rateName,
                    totalNight: ratePrice,
                    breakfast: hasBreakfast,
                    refundable: isRefundable,
                };
            });
            return {
                id: '',
                name: roomName,
                adults: thn.parseNumber(room.querySelector('.config-item')?.textContent),
                children: 0,
                type: roomType,
                prices: roomPrices,
            };
        });
    };
    /**
       * @memberOf module:bs_368
       * @this thn.widgets.price
       * @see {@link Price#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceParseData = function priceParseData(ctx) {
        const widgetData = {};
        const roomsData = document.querySelector('amadeus-hos-res-wc-shop-rooms');
        // Dates
        const dateBegin = roomsData?.['__amadeus-hos-res-attr-date-in'] || thn.dataLayerHelper.get('ihDate');
        const dateEnd = roomsData?.['__amadeus-hos-res-attr-date-out'] || thn.dataLayerHelper.get('ihDateOut');
        if (!dateBegin || !dateEnd) {
            return null;
        }
        widgetData.dateBegin = dateBegin?.replace(/-/g, '/');
        widgetData.dateEnd = dateEnd?.replace(/-/g, '/');
        const nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
        // Guests, Adults & Children && Prices & Currency || No Rooms
        const adults = roomsData?.['__amadeus-hos-res-attr-adults'];
        const children = roomsData?.['__amadeus-hos-res-attr-children'];
        const roomsEl = roomsData?.shadowRoot;
        // We need to exclude rates for some properties
        const excludedRates = /CAA\/AAA|Government of Canada|Senior/i;
        const propertiesToBeExcluded = [1045425, 1045426, 1048872, 1048873].includes(thn.data.propertyId);
        const isUserUsingPriceAfterTaxes = [1044040, 1039630].includes(thn.data.accountUserId);
        const pricesArrays = roomsData?.__originalShopRoomTypeList.map((roomType) => (roomType.ratePlans
            ? [...roomType.ratePlans]
                .filter((rate) => (propertiesToBeExcluded && !rate?.ratePlanName?.match(excludedRates)) || !propertiesToBeExcluded)
                .map((price) => {
                const priceToDisplay = isUserUsingPriceAfterTaxes ? price?.totalRateAfterTax : price?.totalRateToDisplay;
                return priceToDisplay;
            })
            : []));
        const prices = !thn.empty(pricesArrays) && [].concat(...pricesArrays).filter(Boolean);
        const currency = thn.parseCurrency(document.querySelector('.header__currency-switcher .cs-trigger span')?.textContent || roomsData?.['amadeus-hos-res-attr-currency']);
        const noAvailability = roomsEl?.querySelector('.amadeus-hos-res-cn-shop-room-total-unavailability-wrapper');
        widgetData.adults = adults || 2;
        widgetData.children = children || 0;
        if (noAvailability) {
            widgetData.noRooms = true;
            return widgetData;
        }
        if (!thn.empty(prices) && currency && nights) {
            widgetData.price = Math.min(...prices) / nights;
            widgetData.priceType = isUserUsingPriceAfterTaxes ? 'full' : 'base';
            widgetData.currency = currency;
            widgetData.roomDetails = roomDetailsParseData(roomsEl);
            widgetData.set = true;
        }
        return widgetData;
    };
    /**
       * @memberOf module:bs_368
       * @this thn.widgets.booking
       * @see {@link Booking#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.bookingParseData = function bookingParseData(ctx) {
        const widgetData = {};
        if (!window.dataLayer) {
            widgetData.set = false;
            return widgetData;
        }
        const bookingId = thn.dataLayerHelper.get('ihBookedConfirmID');
        if (!bookingId) {
            widgetData.set = false;
            return widgetData;
        }
        widgetData.bookingId = bookingId;
        widgetData.dateBegin = thn.dataLayerHelper.get('ihDate')?.replaceAll('-', '/');
        widgetData.dateEnd = thn.dataLayerHelper.get('ihDateOut')?.replaceAll('-', '/');
        const priceBeforeTaxes = thn.parseNumber(thn.dataLayerHelper.get('ihAmount'));
        const taxes = thn.parseNumber(thn.dataLayerHelper.get('ihTaxes'));
        const priceElm = document.querySelector('.confirmation-page-booking-summary amadeus-hos-res-wc-booking-summary').shadowRoot.querySelector('.reservation-total-amount')?.textContent;
        widgetData.price = (priceBeforeTaxes && taxes && priceBeforeTaxes + taxes) || thn.parseNumber(priceElm);
        widgetData.currency = thn.parseCurrency(thn.dataLayerHelper.get('ihCurrency'));
        return widgetData;
    };
    /**
     * @memberOf module:bs_368
     * @this thn.widgets.price
     * @see {@link Price#get_promocode}
     *
     * @param {object} ctx - The context object.
     */
    engine.getPromocode = function getPromocode(ctx) {
        const roomsData = document.querySelector('amadeus-hos-res-wc-shop-rooms');
        const promocode = roomsData?.['amadeus-hos-res-attr-access-codes']?.[0]?.code;
        ctx.promocode = promocode || '';
        return ctx.promocode;
    };
    engine.onBeforeApplyDiscount = async (_ctx) => {
        // The application of discount codes is done via URL for this Booking Engine
        // however the website will not take the new query string parameter
        // unless the reservations information is removed from the session ¯\_(ツ)_/¯
        // In other words, visiting this example URL only works on the first tab load:
        // https://shadehotel.ihotelier.com/redondo-beach/book/accommodations?discount=THNEXCL
        try {
            const { sessionStorage } = window;
            Object.keys(sessionStorage)
                .filter((key) => key.match(/\d+_reservation$/))
                .forEach((key) => {
                sessionStorage.removeItem(key);
            });
        }
        catch (_) { /* empty */ }
    };
    return engine;
}

export { factory };
